import api from './api';
import { getTranslation } from '../i18n/translations/index.js';
import { API_BASE_URL } from '../lib/constants';

/**
 * 图片相关API服务
 */
class ImageService {
  
  /**
   * 上传图片
   * @param {File} file - 图片文件
   * @returns {Promise} 返回上传结果
   */
  async uploadImage(file) {
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('图片上传失败:', error);
      throw new Error(error.response?.data?.message || getTranslation('imageService.uploadFailed', 'zh-TW'));
    }
  }

  /**
   * 生成AI图片
   * @param {Object} params - 生成参数
   * @param {string} params.prompt - 提示词
   * @param {string} params.negativePrompt - 负面提示词
   * @param {number} params.width - 宽度
   * @param {number} params.height - 高度
   * @param {number} params.steps - 步数
   * @param {number} params.cfgScale - CFG比例
   * @param {number} params.seed - 种子
   * @param {string} params.model - 模型
   * @returns {Promise} 返回生成结果
   */
  async generateImage(params) {
    try {
      const response = await api.post('/generate-image/generate', params);
      return response.data;
    } catch (error) {
      console.error('AI图片生成失败:', error);
      // 直接抛出原始错误，让errorHandler处理结构化错误信息
      throw error;
    }
  }

  /**
   * 编辑图片（基于上传的图片进行AI处理）
   * @param {Object} params - 编辑参数
   * @param {File[]} params.images - 图片文件数组
   * @param {string} params.prompt - 提示词
   * @param {string} params.negativePrompt - 负面提示词
   * @param {string} params.size - 尺寸（例如 '1:1'）
   * @param {number} params.steps - 步数
   * @param {number} params.cfgScale - CFG比例
   * @param {number} params.seed - 种子
   * @param {string} params.model - 模型
   * @returns {Promise} 返回编辑结果
   */
  async editImage(params) {
    try {
      let normalizedParams = params;
      if (normalizedParams?.aspectRatio && typeof normalizedParams.aspectRatio === 'object') {
        const settings = normalizedParams.aspectRatio;
        normalizedParams = {
          ...normalizedParams,
          aspectRatio: settings.aspectRatio || '',
          model: settings.model || normalizedParams.model,
          resolution: settings.resolution || normalizedParams.resolution
        };
      }
      const formData = new FormData();
      
      // 添加图片文件
      if (params.images && params.images.length > 0) {
        params.images.forEach((image, index) => {
          formData.append('images', image);
        });
      }
      
      // 添加其他参数
      formData.append('prompt', params.prompt || '');
      formData.append('negativePrompt', params.negativePrompt || '');
      if (normalizedParams.aspectRatio) {
        formData.append('aspectRatio', normalizedParams.aspectRatio);
      }
      formData.append('steps', params.steps || 20);
      formData.append('cfgScale', params.cfgScale || 7.5);
      if (params.seed) {
        formData.append('seed', params.seed);
      }
      // Add strength parameter for img2img control
      if (params.strength !== undefined) {
        formData.append('strength', params.strength);
      }
      // Add imageStrength parameter if needed
      if (params.imageStrength !== undefined) {
        formData.append('imageStrength', params.imageStrength);
      }
      formData.append('model', normalizedParams.model || 'gemini');
      if (normalizedParams.resolution) {
        formData.append('resolution', normalizedParams.resolution);
      }
      
      // 添加工具ID参数
      if (normalizedParams.toolId) {
        formData.append('toolId', normalizedParams.toolId);
      }
      
      const reservedKeys = [
        'images',
        'prompt',
        'negativePrompt',
        'aspectRatio',
        'steps',
        'cfgScale',
        'seed',
        'strength',
        'imageStrength',
        'model',
        'resolution',
        'toolId',
        'size'
      ];
      Object.keys(normalizedParams).forEach(key => {
        if (reservedKeys.includes(key)) {
          return;
        }
        const value = normalizedParams[key];
        if (value === undefined || value === null) {
          return;
        }
        if (Array.isArray(value)) {
          formData.append(key, value.join(','));
        } else {
          formData.append(key, value);
        }
      });
      
      const response = await api.post('/generate-image/edit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('图片编辑失败:', error);
      // 直接抛出原始错误，让errorHandler处理结构化错误信息
      throw error;
    }
  }

  /**
   * 获取生成状态
   * @param {string} generationId - 生成任务ID
   * @returns {Promise} 返回生成状态
   */
  async getGenerationStatus(generationId) {
    try {
      const response = await api.get(`/generate-image/status/${generationId}`);
      return response.data;
    } catch (error) {
      console.error('获取生成状态失败:', error);
      throw new Error(error.response?.data?.error || getTranslation('imageService.getStatusFailed', 'zh-TW'));
    }
  }

  /**
   * 获取用户的图片生成历史
   * @param {number} page - 页码
   * @param {number} size - 每页大小
   * @returns {Promise} 返回历史记录
   */
  async getGenerationHistory(page = 0, size = 20) {
    try {
      const response = await api.get('/creation-records', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('获取生成历史失败:', error);
      throw new Error(error.response?.data?.message || getTranslation('imageService.getHistoryFailed', 'zh-TW'));
    }
  }

  /**
   * 删除生成记录
   * @param {string} recordId - 记录ID
   * @returns {Promise} 返回删除结果
   */
  async deleteGenerationRecord(recordId) {
    try {
      const response = await api.delete(`/creation-records/${recordId}`);
      return response.data;
    } catch (error) {
      console.error('删除记录失败:', error);
      throw new Error(error.response?.data?.message || getTranslation('imageService.deleteRecordFailed', 'zh-TW'));
    }
  }

  /**
   * 下载图片
   * @param {string} imageUrl - 图片URL
   * @param {string} filename - 文件名
   */
  downloadImage(imageUrl, filename = getTranslation('imageService.defaultFilename', 'zh-TW')) {
    try {
      if (!imageUrl) {
        throw new Error(getTranslation('imageService.imageUrlEmpty', 'zh-TW'));
      }
      
      // 构建完整的图片URL
      const downloadUrl = imageUrl.startsWith('http') 
        ? imageUrl 
        : `${API_BASE_URL}${imageUrl}`;
      
      // 使用fetch下载图片并创建blob对象，确保强制下载
      fetch(downloadUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(getTranslation('imageService.downloadFailed', 'zh-TW'));
          }
          return response.blob();
        })
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(error => {
          console.error('下载图片失败:', error);
          // 如果fetch失败，回退到直接链接方法
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    } catch (error) {
      console.error('下载图片失败:', error);
      throw error;
    }
  }

  /**
   * 验证图片文件
   * @param {File} file - 图片文件
   * @returns {Object} 验证结果
   */
  validateImageFile(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    if (!file) {
      return { valid: false, error: getTranslation('imageService.selectImageFile', 'zh-TW') };
    }
    
    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: getTranslation('imageService.unsupportedFormat', 'zh-TW') };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: getTranslation('imageService.fileTooLarge', 'zh-TW') };
    }
    
    return { valid: true };
  }

  /**
   * 将文件转换为Base64
   * @param {File} file - 文件对象
   * @returns {Promise<string>} Base64字符串
   */
  fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // 移除data:image/...;base64,前缀，只保留base64数据
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * 创建图片预览URL
   * @param {File} file - 图片文件
   * @returns {string} 预览URL
   */
  createImagePreviewUrl(file) {
    return URL.createObjectURL(file);
  }

  /**
   * 释放图片预览URL
   * @param {string} url - 预览URL
   */
  revokeImagePreviewUrl(url) {
    URL.revokeObjectURL(url);
  }
}

const imageService = new ImageService();
export default imageService;
